import { HelpCircle } from 'lucide-react'

interface CheckboxProps {
  label: string
  value: boolean
  onChange: (value: boolean) => void
  tooltip?: string
}

export function Checkbox({ label, value, onChange, tooltip }: CheckboxProps) {
  return (
    <label className="flex items-center gap-2 cursor-pointer group">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-600 bg-surface-900 text-primary-500 focus:ring-primary-500/50"
      />
      <span className="text-sm text-gray-300 group-hover:text-gray-100 transition-colors">
        {label}
      </span>
      {tooltip && (
        <div className="relative group/tip">
          <HelpCircle className="w-3 h-3 text-gray-500 group-hover/tip:text-gray-300" />
          <div className="absolute hidden group-hover/tip:block z-50 w-48 p-2 text-xs bg-surface-700 border border-white/10 rounded-lg shadow-xl -top-2 left-5">
            {tooltip}
          </div>
        </div>
      )}
    </label>
  )
}

interface NumberSliderProps {
  label: string
  value: number | null
  onChange: (value: number | null) => void
  min: number
  max: number
  step: number
  allowNull?: boolean
  nullLabel?: string
  tooltip?: string
}

export function NumberSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  allowNull = false,
  nullLabel = 'Auto',
  tooltip,
}: NumberSliderProps) {
  const displayValue = value ?? min

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm text-gray-400 flex items-center gap-1">
          {label}
          {tooltip && (
            <div className="relative group/tip">
              <HelpCircle className="w-3 h-3 text-gray-500 group-hover/tip:text-gray-300" />
              <div className="absolute hidden group-hover/tip:block z-50 w-48 p-2 text-xs bg-surface-700 border border-white/10 rounded-lg shadow-xl -top-2 left-5">
                {tooltip}
              </div>
            </div>
          )}
        </label>
        <div className="flex items-center gap-2">
          {allowNull && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className={`px-2 py-0.5 text-xs rounded ${
                value === null
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50'
                  : 'bg-surface-700/50 text-gray-400 border border-white/5 hover:border-white/10'
              }`}
            >
              {nullLabel}
            </button>
          )}
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
            min={min}
            max={max}
            step={step}
            className="w-16 px-2 py-0.5 text-sm bg-surface-800/50 border border-white/5 rounded focus:border-primary-500 focus:outline-none"
          />
        </div>
      </div>
      <input
        type="range"
        value={displayValue}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        min={min}
        max={max}
        step={step}
        className="w-full h-1 bg-surface-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
      />
    </div>
  )
}

interface SelectProps {
  label: string
  value: string | number
  options: { value: string | number; label: string }[]
  onChange: (value: string | number) => void
  tooltip?: string
}

export function Select({ label, value, options, onChange, tooltip }: SelectProps) {
  return (
    <div className="space-y-1">
      <label className="text-sm text-gray-400 flex items-center gap-1">
        {label}
        {tooltip && (
          <div className="relative group/tip">
            <HelpCircle className="w-3 h-3 text-gray-500 group-hover/tip:text-gray-300" />
            <div className="absolute hidden group-hover/tip:block z-50 w-48 p-2 text-xs bg-surface-700 border border-white/10 rounded-lg shadow-xl -top-2 left-5">
              {tooltip}
            </div>
          </div>
        )}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm bg-surface-800/50 border border-white/5 rounded-lg focus:border-primary-500 focus:outline-none"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}

interface ButtonGroupProps {
  label: string
  value: string | number
  options: { value: string | number; label: string }[]
  onChange: (value: string | number) => void
  tooltip?: string
}

export function ButtonGroup({ label, value, options, onChange, tooltip }: ButtonGroupProps) {
  return (
    <div className="space-y-2">
      <label className="text-sm text-gray-400 flex items-center gap-1">
        {label}
        {tooltip && (
          <div className="relative group/tip">
            <HelpCircle className="w-3 h-3 text-gray-500 group-hover/tip:text-gray-300" />
            <div className="absolute hidden group-hover/tip:block z-50 w-48 p-2 text-xs bg-surface-700 border border-white/10 rounded-lg shadow-xl -top-2 left-5">
              {tooltip}
            </div>
          </div>
        )}
      </label>
      <div className="flex gap-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
              value === opt.value
                ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50'
                : 'bg-surface-700/50 text-gray-400 border border-white/5 hover:bg-surface-700/80 hover:border-white/10'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

interface TextInputProps {
  label: string
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  tooltip?: string
}

export function TextInput({ label, value, onChange, placeholder, tooltip }: TextInputProps) {
  return (
    <div className="space-y-1">
      <label className="text-sm text-gray-400 flex items-center gap-1">
        {label}
        {tooltip && (
          <div className="relative group/tip">
            <HelpCircle className="w-3 h-3 text-gray-500 group-hover/tip:text-gray-300" />
            <div className="absolute hidden group-hover/tip:block z-50 w-48 p-2 text-xs bg-surface-700 border border-white/10 rounded-lg shadow-xl -top-2 left-5">
              {tooltip}
            </div>
          </div>
        )}
      </label>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm bg-surface-800/50 border border-white/5 rounded-lg focus:border-primary-500 focus:outline-none"
      />
    </div>
  )
}
